<template>
  <v2-modal
    :show="show"
    :title="`${editData ? 'Update' : 'Invite'} Additional User`"
    size="md"
    @close="$emit('close')"
  >
    <div class="v2-invite-form">
      <div class="v2-invite-form__row">
        <v2-input
          :value="form.firstName"
          label="First Name"
          placeholder="Enter First Name"
          :error="errors.firstName"
          @input="form.firstName = $event"
          @blur="validateField('firstName')"
        />
        <v2-input
          :value="form.lastName"
          label="Last Name"
          placeholder="Enter Last Name"
          :error="errors.lastName"
          @input="form.lastName = $event"
          @blur="validateField('lastName')"
        />
      </div>

      <v2-input
        :value="form.email"
        label="Email"
        placeholder="Enter Email"
        type="email"
        :disabled="!!editData"
        :error="errors.email"
        @input="form.email = $event"
        @blur="validateField('email')"
      />

      <div class="v2-invite-form__field">
        <label class="v2-invite-form__label">Role</label>
        <v2-dropdown
          :options="roleOptions"
          :value="form.role"
          placeholder="Select role"
          label-key="label"
          value-key="value"
          @select="form.role = $event"
        />
      </div>

      <div
        v-if="isEntityAdmin && form.role === 'salesRepresentative'"
        class="v2-invite-form__field"
      >
        <label class="v2-invite-form__label">Reporting Manager</label>
        <v2-dropdown
          :options="managerOptions"
          :value="form.reportingTo ? form.reportingTo.userId : ''"
          placeholder="Select reporting manager"
          label-key="label"
          value-key="value"
          :searchable="true"
          @select="onSelectManager($event)"
        />
      </div>
    </div>

    <template #footer>
      <v2-button
        variant="outline"
        @click="$emit('close')"
      >
        Cancel
      </v2-button>
      <v2-button
        :disabled="isSubmitDisabled"
        :loading="isSaving"
        @click="onSubmit"
      >
        {{ editData ? 'Update' : 'Create' }}
      </v2-button>
    </template>
  </v2-modal>
</template>

<script>
import { USER_ROLES, USER_ROLES_LABEL } from '@/constants'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'V2InviteTeammateModal',
  components: {
    V2Modal,
    V2Input,
    V2Button,
    V2Dropdown,
  },
  props: {
    show: { type: Boolean, default: false },
    editData: { type: Object, default: null },
    roles: { type: Array, default: () => [] },
    salesManagers: { type: Array, default: () => [] },
    isEntityAdmin: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        reportingTo: null,
      },
      errors: {
        firstName: '',
        lastName: '',
        email: '',
      },
    }
  },
  computed: {
    roleOptions() {
      return this.roles.map(role => ({
        label: USER_ROLES_LABEL[role] || role,
        value: role,
      }))
    },
    managerOptions() {
      return this.salesManagers.map(m => ({
        label: m.firstName || m.name || '-',
        value: m.userId,
      }))
    },
    hasErrors() {
      return !this.form.firstName
        || !this.form.lastName
        || !this.form.email
        || !EMAIL_REGEX.test(this.form.email)
        || !this.form.role
    },
    needsReportingManager() {
      return this.isEntityAdmin && this.form.role === USER_ROLES.SALESREPRESENTATIVE
    },
    isSubmitDisabled() {
      if (this.isSaving) return true
      if (this.hasErrors) return true
      if (this.needsReportingManager && !this.form.reportingTo) return true
      return false
    },
  },
  watch: {
    show(val) {
      if (val && this.editData) {
        this.form.firstName = this.editData.firstName || ''
        this.form.lastName = this.editData.lastName || ''
        this.form.email = this.editData.email || ''
        this.form.role = this.editData.role || ''
        this.form.reportingTo = null
        this.errors = { firstName: '', lastName: '', email: '' }
      } else if (val) {
        this.resetForm()
      }
    },
  },
  methods: {
    validateField(field) {
      if (field === 'firstName') {
        this.errors.firstName = this.form.firstName ? '' : 'First Name is required'
      } else if (field === 'lastName') {
        this.errors.lastName = this.form.lastName ? '' : 'Last Name is required'
      } else if (field === 'email') {
        if (!this.form.email) {
          this.errors.email = 'Email is required'
        } else if (!EMAIL_REGEX.test(this.form.email)) {
          this.errors.email = 'Please enter a valid email'
        } else {
          this.errors.email = ''
        }
      }
    },
    onSelectManager(userId) {
      const manager = this.salesManagers.find(m => m.userId === userId)
      this.form.reportingTo = manager || null
    },
    onSubmit() {
      // Validate all fields
      this.validateField('firstName')
      this.validateField('lastName')
      this.validateField('email')
      if (this.hasErrors) return

      if (this.editData) {
        const payload = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          role: this.form.role,
          _id: this.editData._id,
        }
        this.$emit('update', payload)
      } else {
        const payload = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          role: this.form.role,
        }
        if (this.needsReportingManager && this.form.reportingTo) {
          payload.reportingManagerId = this.form.reportingTo.userId
        }
        this.$emit('invite', payload)
      }
    },
    resetForm() {
      this.form = {
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        reportingTo: null,
      }
      this.errors = { firstName: '', lastName: '', email: '' }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-invite-form {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-4);

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-4);
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }
}
</style>
